<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <div class="flex flex-col gap-y-5">
            <FormInput
              v-model="rawFocusPointTemplate.name"
              :error="errors?.name"
              class="col-start-1 col-end-2"
              type="text"
              :label="$t('focusPointTemplate.name')"
              :placeholder="$t('focusPointTemplate.name')"
            />
            <Multiselect
              v-model="rawFocusPointTemplate.region"
              :error="errors?.region"
              class="col-start-1 col-end-2"
              :options="regions"
              :label="$t('focusPointTemplate.region')"
              :can-deselect="false"
              :can-clear="false"
              :searchable="true"
              @select="setMapCenter()"
            />
            <LocationMap ref="map" v-model="rawFocusPointTemplate.gisLocation" v-model:radius="rawFocusPointTemplate.radius" />
            <div class="flex gap-x-5">
              <FormInput
                :model-value="rawFocusPointTemplate.gisLocation?.lat"
                :error="errors?.gisLocation?.lat"
                class="w-1/2"
                type="text"
                :label="$t('lat')"
                pattern="^\d*\.?\d*$"
                @update:model-value="setCoords(parseFloat($event), rawFocusPointTemplate.gisLocation?.long)"
              />
              <FormInput
                :model-value="rawFocusPointTemplate.gisLocation?.long"
                :error="errors?.gisLocation?.long"
                class="w-1/2"
                type="text"
                :label="$t('long')"
                pattern="^\d*\.?\d*$"
                @update:model-value="setCoords(rawFocusPointTemplate.gisLocation?.lat, parseFloat($event))"
              />
            </div>
            <FormInput
              v-model="rawFocusPointTemplate.radius"
              :error="errors?.radius"
              class="col-start-1 col-end-2"
              type="number"
              :label="$t('focusPointTemplate.radius')"
              :placeholder="$t('focusPointTemplate.radius')"
            />
            <div class="grid grid-cols-2 gap-x-5">
              <FormInput
                v-model="rawFocusPointTemplate.cardinality"
                :error="errors?.cardinality"
                type="number"
                :label="$t('focusPointTemplate.cardinality')"
                :placeholder="$t('focusPointTemplate.cardinality')"
              />
              <FormInput
                v-model="rawFocusPointTemplate.paymentAmount"
                :error="errors?.paymentAmount"
                type="number"
                :label="$t('focusPointTemplate.paymentAmount')"
                :placeholder="$t('focusPointTemplate.paymentAmount')"
              />
            </div>
            <div class="grid grid-cols-2 gap-x-5">
              <InputDuration
                v-model="rawFocusPointTemplate.threshold"
                :error="errors?.threshold"
                type="text"
                class="w-full"
                :label="$t('focusPointTemplate.threshold')"
                placeholder="hh:mm:ss"
              />
              <FormInput
                v-model="rawFocusPointTemplate.numOfPhotos"
                :error="errors?.numOfPhotos"
                type="number"
                :label="$t('focusPointTemplate.numOfPhotos')"
                :placeholder="$t('focusPointTemplate.numOfPhotos')"
              />
            </div>
            <div class="grid grid-cols-2 gap-x-5">
              <DatePicker
                v-model="rawFocusPointTemplate.validityStart"
                :error="errors?.validityStart"
                mode="datetime"
                :label="$t('focusPointTemplate.validityStart')"
              />
              <DatePicker
                v-model="rawFocusPointTemplate.validityEnd"
                :error="errors?.validityEnd"
                mode="datetime"
                :label="$t('focusPointTemplate.validityEnd')"
              />
            </div>
          </div>
        </div>
        <div class="w-full text-center">
          <custom-button
            variant="primary"
            class="mt-15"
            :busy="loading"
            @click="save"
          >
            {{ $t('save') }}
          </custom-button>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import {computed, reactive, ref, watch} from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {date, number, object, string} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import FormInput from '@utils/inputs/Input.vue';
import Multiselect from '@utils/Multiselect.vue';
import DatePicker from '@components/utils/inputs/DatePicker.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import { syncRef } from '@hooks/state';
import CustomButton from '@utils/CustomButton.vue';
import InputDuration from '@utils/inputs/InputDuration.vue';
import { collOptionsProvider } from '@utils/table/hooks/provider';
import LocationMap from '@utils/maps/PickLocationRadiusMap.vue';
import type {Campaign, CampaignGroup, FocusPointTemplate, Region} from '@/entities/Entities';

interface Props {
  open: boolean;
  campaignGroup?: CampaignGroup;
  focusPointTemplate?: FocusPointTemplate;
}

const emit = defineEmits(['update:open', 'update:focusPointTemplate', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalFocusPointTemplate = useVModel(props, 'focusPointTemplate', emit);

const isNew = computed(() => !props.focusPointTemplate);
const tKey = computed(() =>
  (isNew.value
    ? 'pages.templates.focusPointTemplatesList.modal.new'
    : 'pages.templates.focusPointTemplatesList.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

const initialData = (focusPointTemplate?: FocusPointTemplate) =>
  (focusPointTemplate
    ? {
      ...focusPointTemplate,
      region: focusPointTemplate.region['@id'],
    }
    : {
      region: undefined,
      name: undefined,
      radius: undefined,
      threshold: undefined,
      cardinality: undefined,
      paymentAmount: undefined,
      numOfPhotos: undefined,
      gisLocation: undefined,
      validityStart: undefined,
      validityEnd: undefined,
    });

const rawFocusPointTemplate = syncRef(internalFocusPointTemplate, (focusPointTemplate) => ({
  ...initialData(focusPointTemplate),
}));

const fetchRegions = collOptionsProvider<Region>('/regions', (item) => ({
  label: item.name,
  value: item['@id'],
  gisLocation: item.gisLocation,
}));

const regions = ref<{ label: string, value: string, gisLocation?: { lat: number, long: number } }[]>([]);
const loadRegions = async () => {
  regions.value = await fetchRegions();
};

loadRegions();

const isPrice = (value: undefined|number) => {
  if (value !== undefined) {
    return /^\d+(\.\d{0,2})?$/.test(value.toString());
  }
  return true;
};

const {
  data: focusPointTemplate,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    region: string().nullable().required(t('validation.notEmpty')),
    name: string().min(1, t('validation.notEmpty')).nullable().required(t('validation.notEmpty')),
    radius: number()
      .positive(t('validation.positive'))
      .integer(t('validation.integer'))
      .typeError(t('validation.number'))
      .nullable()
      .required(t('validation.notEmpty')),
    threshold: number()
      .integer(t('validation.integer'))
      .positive(t('validation.positive'))
      .typeError(t('validation.number'))
      .nullable(),
    cardinality: number()
      .integer(t('validation.integer'))
      .positive(t('validation.positive'))
      .typeError(t('validation.number'))
      .nullable(),
    paymentAmount: number()
      .typeError(t('validation.number'))
      .positive(t('validation.positive'))
      .test('is-price', t('validation.price'), isPrice)
      .nullable(),
    numOfPhotos: number()
      .integer(t('validation.integer'))
      .positive(t('validation.positive'))
      .typeError(t('validation.number'))
      .nullable(),
    gisLocation: object({
      lat: number().nullable().required(t('validation.notEmpty')),
      long: number().nullable().required(t('validation.notEmpty')),
    }),
    validityStart: date().nullable(),
    validityEnd: date().nullable(),
  }),
  rawFocusPointTemplate,
);

function resetData() {
  rawFocusPointTemplate.value = { ...initialData() } as unknown as typeof focusPointTemplate['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalFocusPointTemplate.value = undefined;
}

const map = ref();

const setMapCenter = () => {
  const gisLocation = regions.value.find((region) => region.value === rawFocusPointTemplate.value?.region)?.gisLocation;
  map.value?.moveToCenter(gisLocation?.lat, gisLocation?.long);
};

const setCoords = (lat: number|undefined, long: number|undefined) => {
  rawFocusPointTemplate.value.gisLocation = {
    lat: lat || 0,
    long: long || 0,
  };
};

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const edit = !!props.focusPointTemplate;
    const url = edit ? props.focusPointTemplate['@id'] : '/focus_point_templates';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        name: focusPointTemplate.value.name,
        region: focusPointTemplate.value.region,
        radius: focusPointTemplate.value.radius,
        threshold: focusPointTemplate.value.threshold ?? null,
        cardinality: focusPointTemplate.value.cardinality ?? null,
        paymentAmount: focusPointTemplate.value.paymentAmount ?? null,
        numOfPhotos: focusPointTemplate.value.numOfPhotos ?? null,
        gisLocation: focusPointTemplate.value.gisLocation,
        validityStart: focusPointTemplate.value.validityStart,
        validityEnd: focusPointTemplate.value.validityEnd,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});
</script>

<style lang="scss" scoped></style>
